/* ═══════════════════════════ backup ═══════════════════════════

   A term's work lives in one browser on one machine, and browsers throw that
   away for all sorts of ordinary reasons. This is the way out and the way
   across: a file you can keep, mail to yourself, or carry to a phone.

   It is also, deliberately, the whole of the sharing story. Two people who
   want the same planner do not need accounts and a database between them —
   they need a file. Anything more is a real project, and should be started
   because it is wanted rather than because the alternative was never built. */

import { KEYS, storage } from './storage.js';

export const BACKUP_FORMAT = 'studyhub-backup';
export const BACKUP_VERSION = 1;

/**
 * Everything, as one JSON string.
 *
 * The envelope carries a format tag and a version so a file found in a
 * downloads folder in two years is identifiable, and so import can refuse
 * something that merely happens to be JSON.
 */
export function exportAll(at = new Date()) {
  const s = storage();
  const data = {};
  for (const key of Object.values(KEYS)) {
    const raw = s.read(key);
    if (raw == null) continue;
    // Kept as parsed values, not embedded strings, so the file is legible to a
    // human who opens it — the point of a backup is that you can check it.
    try { data[key] = JSON.parse(raw); } catch { data[key] = raw; }
  }

  return JSON.stringify({
    format: BACKUP_FORMAT,
    version: BACKUP_VERSION,
    exportedAt: at.toISOString(),
    data,
  }, null, 2);
}

/** `studyhub-2026-09-14.json` — sorts correctly and says what it is. */
export function backupFilename(at = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `studyhub-${at.getFullYear()}-${p(at.getMonth() + 1)}-${p(at.getDate())}.json`;
}

/**
 * Read a backup and describe what is in it, without writing anything.
 *
 * Import is the one irreversible thing in the app — it overwrites a term — so
 * it happens in two steps. This is the first: parse, validate, and report what
 * would be replaced, so the confirmation can name real numbers instead of
 * asking the user to accept an unspecified loss.
 *
 * @returns {BackupInspection}
 */
export function inspectBackup(text) {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, why: 'That file is not JSON. Pick the .json file studyHub exported.' };
  }

  if (!parsed || typeof parsed !== 'object' || parsed.format !== BACKUP_FORMAT) {
    return { ok: false, why: 'That is not a studyHub backup — it has no studyHub header.' };
  }
  if (typeof parsed.version !== 'number' || parsed.version > BACKUP_VERSION) {
    return {
      ok: false,
      why: `That backup was written by a newer studyHub (format ${parsed.version}). Update this copy first.`,
    };
  }
  if (!parsed.data || typeof parsed.data !== 'object' || !parsed.data[KEYS.main]) {
    return { ok: false, why: 'That backup has no studyHub state in it.' };
  }

  const main = parsed.data[KEYS.main] || {};
  const count = (v) => (Array.isArray(v) ? v.length : 0);

  return {
    ok: true,
    parsed,
    summary: {
      exportedAt: typeof parsed.exportedAt === 'string' ? parsed.exportedAt : null,
      courses: count(main.courses),
      tasks: count(main.tasks),
      deadlines: count(main.deadlines),
      events: count(main.events),
      thoughts: count(main.thoughts),
      images: count(parsed.data[KEYS.images]),
    },
  };
}

/**
 * Write an inspected backup over what is there.
 *
 * Takes the object `inspectBackup` returned rather than raw text, so there is
 * no path to this function that has not been through validation.
 */
export function restoreBackup(parsed) {
  const s = storage();
  for (const key of Object.values(KEYS)) {
    const value = parsed.data[key];
    if (value === undefined) { s.remove(key); continue; }
    s.write(key, typeof value === 'string' ? value : JSON.stringify(value));
  }
}

/** How a backup reads in a sentence, for the confirmation. */
export function describeBackup(summary) {
  const when = summary.exportedAt
    ? new Date(summary.exportedAt).toLocaleString(undefined,
        { dateStyle: 'medium', timeStyle: 'short' })
    : 'an unknown date';
  const bits = [
    `${summary.tasks} task${summary.tasks === 1 ? '' : 's'}`,
    `${summary.courses} course${summary.courses === 1 ? '' : 's'}`,
    `${summary.deadlines} deadline${summary.deadlines === 1 ? '' : 's'}`,
  ];
  return `Saved ${when}, holding ${bits.join(', ')}.`;
}
